// <reference types="@angular/localize" />

import {
  HTTP_INTERCEPTORS,
  provideHttpClient,
  withInterceptorsFromDi
} from '@angular/common/http';

import {
  MsalInterceptor,
  MsalService,
  MsalGuard,
  MsalBroadcastService,
  MsalModule,
} from '@azure/msal-angular';

import {
  IPublicClientApplication,
  PublicClientApplication,
  InteractionType,
  BrowserCacheLocation,
  LogLevel,
} from '@azure/msal-browser';

import { environment } from './environments/environment';
import { provideAnimations } from '@angular/platform-browser/animations';
import { JwtInterceptor } from './app/services/jwt-interceptor';
import { GlobalErrorInterceptor } from './app/services/global-error.interceptor';
import { ErrorHandler, importProvidersFrom, inject, provideAppInitializer, provideZoneChangeDetection } from '@angular/core';
import { AppErrorHandler } from './app/app-error-handler';
import { AuthService } from './app/services/auth.service';
import { SignalRService } from './app/services/signalr.service';
import { ConfigStoreService } from './app/services/config.store.service';
import { CustomerService } from './app/features/customer/customer.service';
import { CustomerAdministrationService } from './app/features/admin/customer-administration/customer-administration.service';
import { DashboardService } from './app/features/dashboard/dashboard.service';
import { MainStore } from './app/common/main.store';
import { ModalService } from './app/services/modal.service';
import { DepartmentService } from './app/services/department.service';
import { DbTemplateService } from './app/features/admin/db-template/db-template.service';
import { TemplateService } from './app/features/template/template.service';
import { CommonService } from './app/services/common.service';
import { EventsService } from './app/features/events/events.service';
import { ResourceAccessService } from './app/features/resource-access/resource-access.service';
import { SynchronizationCenterService } from './app/features/admin/synchronization-center/synchronization-center.service';
import { BrowserModule, bootstrapApplication } from '@angular/platform-browser';
import { AppRoutingModule } from './app/app-routing.module';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { ToastrModule } from 'ngx-toastr';
import { NgbModule } from '@ng-bootstrap/ng-bootstrap';
import { AppComponent } from './app/app.component';

// fra App module (MSAL setuo)
export function loggerCallback(logLevel: LogLevel, message: string) {
  // console.log(message);
}

export function MSALInstanceFactory(): IPublicClientApplication {
  return new PublicClientApplication({
    auth: {
      clientId: environment.msalConfig.auth.clientId,
      authority: environment.msalConfig.auth.authority,
      postLogoutRedirectUri: '/',
      redirectUri: '/',
    },
    cache: {
      cacheLocation: BrowserCacheLocation.LocalStorage,
    },
    system: {
      loggerOptions: {
        loggerCallback,
        logLevel: LogLevel.Info,
        piiLoggingEnabled: false,
      },
    },
  });
}

// Interceptor config
const protectedResourceMap = new Map<string, Array<string>>();
protectedResourceMap.set(
  environment.msalConfig.graphUri,
  environment.msalConfig.scopes
);

// Initialize før app loader
export function initializeMsal(msalService: MsalService) {
  return () => msalService.initialize();
}

bootstrapApplication(AppComponent, {
  providers: [
    provideZoneChangeDetection(),importProvidersFrom(
      BrowserModule,
      FormsModule,
      ReactiveFormsModule,
      NgbModule,
      ToastrModule.forRoot(),
      AppRoutingModule,
      MsalModule.forRoot(
      MSALInstanceFactory(),
        {
          interactionType: InteractionType.Redirect,
          authRequest: { scopes: [...environment.msalConfig.scopes] },
          loginFailedRoute: '/login-failed',
        },
        {
          interactionType: InteractionType.Redirect,
          protectedResourceMap,
        }
      )
    ),
    // Outermost interceptor: catches errors surfacing from MSAL/JWT and the backend, routes them through
    // the global ErrorService, and rethrows so existing component .catch()es keep working.
    { provide: HTTP_INTERCEPTORS, useClass: GlobalErrorInterceptor, multi: true },
    { provide: HTTP_INTERCEPTORS, useClass: MsalInterceptor, multi: true },
    { provide: HTTP_INTERCEPTORS, useClass: JwtInterceptor, multi: true },
    { provide: ErrorHandler, useClass: AppErrorHandler },
    provideAnimations(),
    provideAppInitializer(() => inject(MsalService).initialize()),
    MsalService,
    MsalGuard,
    MsalBroadcastService,
    // Fetch backend (the v21+ default — no withXhr()). Nothing in the app uses upload-progress
    // events, which is the only reason to opt back into XHR, and fetch is what carries the
    // per-request options on HttpResourceRequest (cache, credentials, priority, keepalive).
    provideHttpClient(withInterceptorsFromDi()),
    AuthService,
    SignalRService,
    ConfigStoreService,
    CustomerService,
    CustomerAdministrationService,
    DashboardService,
    MainStore,
    ModalService,
    DepartmentService,
    DbTemplateService,
    TemplateService,
    CommonService,
    EventsService,
    ResourceAccessService,
    SynchronizationCenterService,
  ],
}).catch((err) => console.error(err));
